import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Box,
  Divider,
  Chip,
  Link,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { getFormattedDateUTC } from '../../helpers/date-helpers';
import { useUser } from '../../../contexts/UserContext';
import Markdown from 'markdown-to-jsx'

// Define styles using makeStyles for MUI v4
const useStyles = makeStyles((theme) => ({
  card: {
    maxWidth: 1200,
    margin: '20px auto',
    boxShadow: theme.shadows[3],
  },
  chip: {
    marginLeft: theme.spacing(1),
  },
  responseBox: {
    whiteSpace: 'pre-wrap',
    maxHeight: 200,
    overflowY: 'auto',
    marginTop: theme.spacing(1),
  },
  cardActions: {
    justifyContent: 'flex-end',
    padding: theme.spacing(2),
  },
}));

export default function ValidationCard({
  validation,
  fetchParams,
  selectRow,
  getNextRowId,
  updateValidationStatus,
  setValidation,
  open,
}) {

  const {user} = useUser();
  const classes = useStyles();
  const [loadingAction, setLoadingAction] = useState(false);

  // Handle marking the validation as checked
  const handleValidationStatusChange = async (newStatus) => {
    if (!validation) return;
    setLoadingAction(true);
    try {
      setValidation({ ...validation, human_validation_status: newStatus, validated_by_user_id: newStatus == "Checked" ? user.id : null });
      await updateValidationStatus(validation.id, newStatus);
    } catch (error) {
      console.error('Error marking validation as checked:', error);
    } finally {
      setLoadingAction(false);
    }
  };

  // Handle navigating to the next validation
  const handleNextValidation = () => {
    const nextRowId = getNextRowId();
    if (nextRowId) {
      selectRow(nextRowId);
    }
  };

  // If no validation is selected or card is not open, return null
  if (!validation) return null;

  return (
    <Card className={classes.card}>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          Validation #{validation.id} {open ? "⚠" : "✅"}
        </Typography>
        <Divider style={{ marginBottom: 16 }} />
        <Box display="flex" flexDirection="column" gridGap={12}>
          <Typography variant="body1">
            <strong>Utility Bill ID:</strong>{' '}
            <Link
              href={`https://live.energyprint.com/admin/utility_accounts/${validation.utility_account_id}/utility_bills/new?edit_bill_id=${validation.utility_bill_id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {validation.utility_bill_id}
            </Link>
          </Typography>
          <Typography variant="body1">
            <strong>Utility Account:</strong>{' '}
            <Link
              href={`https://live.energyprint.com/admin/utility_accounts/${validation.utility_account_id}/edit`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {validation.acct_num}
            </Link>
          </Typography>
          <Typography variant="body1">
            <strong>Bill Entered By:</strong>{' '}
            {validation.created_by_user || 'System 🤖'}
          </Typography>
          <Typography variant="body1">
            <strong>Textract Bill ID:</strong>{' '}
            <Link
              href={`/${process.env.REACT_APP_MODE == 'invoiage' ? 'documents' : 'invoiage'}/${validation.textract_bill_id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {validation.textract_bill_id}
            </Link>
          </Typography>
          <Typography variant="body1">
            <strong>Severity:</strong>{' '}
            <Chip
              label={validation.severity}
              color={validation.severity === 'VALID' ? 'primary' : 'secondary'}
              size="small"
              className={classes.chip}
            />
          </Typography>
          <Typography variant="body1">
            <strong>Validation Status:</strong>{' '}
            <Chip
              label={validation.human_validation_status}
              color={
                validation.human_validation_status === 'Checked'
                  ? 'primary'
                  : 'default'
              }
              size="small"
              className={classes.chip}
            />
          </Typography>
          <Typography variant="body1">
            <strong>Confidence:</strong> {validation.confidence}%
          </Typography>
          <Typography variant="body1">
            <strong>Validated At:</strong>{' '}
            <span>{new Date(validation.updated_at).toLocaleDateString()} </span>
            <span style={{ whiteSpace: 'nowrap' }}>{new Date(validation.updated_at).toLocaleTimeString()}</span>
          </Typography>
          <Typography variant="body1">
            <strong>Validated By:</strong>{' '}
            {validation.validated_by_user || 'N/A'}
          </Typography>
          {validation.response && (
            <>
              <Typography variant="h6" style={{ marginTop: 16 }}>
                Validation Response:
              </Typography>
              <Markdown
              >
                {validation.response}
              </Markdown>
            </>
          )}
          {validation.next_steps && (
            <>
              <Typography variant="h6" style={{ marginTop: 16 }}>
                Next Steps:
              </Typography>
              <Typography
                variant="body2"
                className={classes.responseBox}
              >
                {validation.next_steps}
              </Typography>
            </>
          )}
        </Box>
      </CardContent>
      <CardActions className={classes.cardActions}>
        { validation.human_validation_status !== 'Checked'
        ? <>
            <Button
              variant="contained"
              color="primary"
              onClick={_ => handleValidationStatusChange("Checked")}
              disabled={
                loadingAction
              }
            >
              Mark as Checked
            </Button>
          </>
        : <>
          <Button
            variant="contained"
            color="primary"
            onClick={_ => handleValidationStatusChange("Unchecked")}
            disabled={
              loadingAction 
            }
          >
            Mark as Unchecked
          </Button>
            
            
        </>
        }
      </CardActions>
    </Card>
  );
}